import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  DateField,
  Field,
  Filters,
  Page,
  PageHeader,
  SearchSelect,
  Table,
  readAttachmentContent,
} from '@evogard/ui';
import {
  businessDate,
  formatDate,
  fromColumn,
  parseMoneyInput,
  sum,
  toColumn,
  ZERO,
  type Money,
  type SettlementMethod,
} from '@evogard/domain';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type { DispatchRow, PendingSettlements, Settlement } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { ApprovalBoard } from './ApprovalBoard.js';

/** As duas abas de Pagamentos (02/11/2026), no `?aba=` do endereço. */
export const SETTLEMENT_TABS = ['a_fechar', 'aprovacao'] as const;
export type SettlementTab = (typeof SETTLEMENT_TABS)[number];
import { ASSISTANCE_KEY, SETTLEMENTS_KEY, reais, when } from './assistance.js';

// @valor
const METHOD_LABEL: Record<SettlementMethod, string> = {
  pix: 'Pix',
  dinheiro: 'Dinheiro',
  cartao: 'Cartão',
};

async function fileBody(file: File) {
  return {
    nome: file.name,
    typeMime: file.type || 'application/octet-stream',
    content: await readAttachmentContent(file),
  };
}

type MonthlyRow = PendingSettlements['monthly'][number];
type MonthlyDispatch = MonthlyRow['dispatches'][number];

/** `AAAA-MM` de um instante, no fuso da casa — o mês do acionamento. */
const monthOf = (instant: string) => businessDate(new Date(instant)).slice(0, 7);
const monthLabel = (ym: string) => `${ym.slice(5, 7)}/${ym.slice(0, 4)}`;
/** O valor de um acionamento, que a API sempre manda legível. */
const moneyOf = (text: string | null): Money =>
  text === null ? ZERO : (fromColumn(text) ?? ZERO);

/**
 * Os pagamentos aos prestadores (`assistencia-23`, `-24`, e o redesenho de
 * 30/10/2026).
 *
 * À vista: o líder já pagou pela Stone e informa um acionamento por vez, com a
 * data e o comprovante. A despesa vai ao Financeiro em Aguardando Pagamento e
 * ao Conta Azul em aberto, e a baixa vem da conciliação da Stone. Mensal: a
 * líder marca os acionamentos de um prestador, de qualquer mês, e gera o lote;
 * a despesa nasce em Aprovação Financeiro, sem vencimento, e o Financeiro
 * revisa e paga.
 */
export function SettlementsScreen({
  tab = 'a_fechar',
  onTab,
}: {
  tab?: SettlementTab;
  onTab: (tab: SettlementTab) => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canPay = can(context, 'assistance.settlements', 'pagar');
  const canClose = can(context, 'assistance.settlements', 'fechar');
  /*
   * A aprovação do supervisor (02/11/2026) era a tela "Despesas", e veio para
   * cá como a segunda aba. Quem não supervisiona não vê a aba — nem as abas:
   * com uma só, a tela é a de sempre.
   */
  const canSupervise = can(context, 'assistance.settlements', 'supervisionar_despesa');

  const pending = useQuery({
    queryKey: [...SETTLEMENTS_KEY, tenantId, 'pendentes'],
    enabled: tenantId !== undefined,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.pendingSettlements(),
        200,
        'Não foi possível carregar o que falta pagar.',
      ),
  });
  const history = useQuery({
    queryKey: [...SETTLEMENTS_KEY, tenantId, 'feitos'],
    enabled: tenantId !== undefined,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.listSettlements(),
        200,
        'Não foi possível carregar os acertos.',
      ),
  });

  /* O que falta fechar, e o histórico do que já foi fechado (os acertos). */
  const toClose = (
    <>
      {(pending.error ?? history.error) && (
        <Alert tone="erro">{(pending.error ?? history.error)!.message}</Alert>
      )}
      {(pending.data?.awaitingAmount.length ?? 0) > 0 && (
        <AwaitingCard
          rows={pending.data!.awaitingAmount}
          canInform={can(context, 'assistance.dispatches', 'informar_valor')}
        />
      )}
      <CashCard
        rows={pending.data?.cash ?? []}
        loading={pending.isLoading}
        canPay={canPay}
      />
      <MonthlyCard
        rows={pending.data?.monthly ?? []}
        loading={pending.isLoading}
        canClose={canClose}
      />
      <Card title="Acertos feitos" bleed>
        <Table
          paginate={false}
          label="Acertos feitos"
          items={history.data ?? []}
          loading={history.isLoading}
          rowKey={(s) => String(s.id)}
          empty="Nenhum acerto ainda."
          columns={[
            {
              title: 'Prestador',
              alignment: 'inicio' as const,
              cell: (s: Settlement) => <b>{s.providerName}</b>,
            },
            {
              title: 'Tipo',
              cell: (s: Settlement) => (
                <Badge tone="informacao">
                  {s.kind === 'a_vista'
                    ? `À vista · ${s.method ? METHOD_LABEL[s.method] : ''}${s.paidOn ? ` · ${formatDate(s.paidOn)}` : ''}`
                    : s.competence
                      ? `Mês ${monthLabel(s.competence)}`
                      : 'Lote mensal'}
                </Badge>
              ),
            },
            { title: 'Acionamentos', cell: (s: Settlement) => s.dispatchCount },
            {
              title: 'Total',
              alignment: 'fim' as const,
              cell: (s: Settlement) => (
                <span className="tabular-nums">{reais(s.total)}</span>
              ),
            },
            {
              title: 'Quando',
              cell: (s: Settlement) => `${when(s.closedAt)} · ${s.closedByName ?? ''}`,
            },
            {
              title: 'Financeiro',
              cell: (s: Settlement) =>
                s.financialExpenseId ? `Despesa nº ${s.financialExpenseId}` : '—',
            },
          ]}
        />
      </Card>
    </>
  );

  return (
    <Page measure="cheia">
      <PageHeader title="Pagamentos aos prestadores">
        O à vista o líder da Assistência 24h já pagou pela Stone, e informa aqui; a
        despesa vai ao Conta Azul e é baixada na conciliação. O mensal vira um lote que o
        Financeiro revisa e paga. Pago, o acionamento não muda mais de valor.
      </PageHeader>
      {canSupervise ? (
        <Tabs value={tab} onValueChange={(v) => onTab(v as SettlementTab)}>
          <TabsList>
            <TabsTrigger value="a_fechar">A fechar</TabsTrigger>
            <TabsTrigger value="aprovacao">Aprovação do supervisor</TabsTrigger>
          </TabsList>
          <TabsContent value="a_fechar" className="mt-4 flex flex-col gap-5">
            {toClose}
          </TabsContent>
          <TabsContent value="aprovacao" className="mt-4">
            <ApprovalBoard />
          </TabsContent>
        </Tabs>
      ) : (
        toClose
      )}
    </Page>
  );
}

type AwaitingRow = PendingSettlements['awaitingAmount'][number];

/**
 * O a combinar sem valor (02/11/2026), só o à vista: executado, e fora do
 * pagamento até alguém informar o que o prestador cobrou. O mensal sem valor
 * não vem aqui: ele está no Mensal, e a líder digita o valor ao montar o lote.
 */
function AwaitingCard({ rows, canInform }: { rows: AwaitingRow[]; canInform: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Card
      title="Aguardando valor"
      descricao={
        canInform
          ? 'Serviços à vista a combinar, já executados. Informe o valor que o prestador cobrou: só depois ele entra no pagamento.'
          : 'Serviços à vista a combinar, já executados. Informar o valor é de quem fecha os pagamentos da assistência.'
      }
      bleed
    >
      <Table
        paginate={false}
        label="Aguardando valor"
        items={rows}
        rowKey={(d) => String(d.id)}
        empty="Nada esperando valor."
        expanded={canInform && open !== null ? String(open) : null}
        onExpandedChange={(key) => setOpen(key === null ? null : Number(key))}
        expand={(d) =>
          canInform ? <InformForm d={d} onDone={() => setOpen(null)} /> : null
        }
        columns={[
          {
            title: 'Acionamento',
            alignment: 'inicio' as const,
            cell: (d: AwaitingRow) => <span className="font-mono text-xs">{d.code}</span>,
          },
          {
            title: 'Prestador',
            cell: (d: AwaitingRow) =>
              `${d.providerName} · ${d.paymentMode === 'a_vista' ? 'à vista' : 'mensal'}`,
          },
          {
            title: 'Serviço',
            cell: (d: AwaitingRow) => `${d.serviceName}${d.plate ? ` · ${d.plate}` : ''}`,
          },
          { title: 'Quando', cell: (d: AwaitingRow) => when(d.executedAt) },
          {
            title: '',
            cell: (d: AwaitingRow) =>
              canInform && (
                <Button size="sm" onClick={() => setOpen(open === d.id ? null : d.id)}>
                  {open === d.id ? 'Fechar' : 'Informar valor'}
                </Button>
              ),
          },
        ]}
      />
    </Card>
  );
}

function InformForm({ d, onDone }: { d: AwaitingRow; onDone: () => void }) {
  const client = useQueryClient();
  const [text, setText] = useState('');
  const amount = parseMoneyInput(text);
  const inform = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.informAmount({
          params: { id: d.id },
          body: { amount: toColumn(amount!) },
        }),
        200,
        'Não foi possível gravar o valor.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
      onDone();
    },
  });
  return (
    <div className="flex flex-col gap-3 p-3">
      {inform.error && <Alert tone="erro">{inform.error.message}</Alert>}
      <div className="grid items-end gap-3 md:grid-cols-[14rem_auto]">
        <Field
          label="Valor que o prestador cobrou"
          mask="money"
          prefix="R$"
          value={text}
          onChange={(e) => setText(e.target.value)}
          help="Fica gravado no acionamento, com o seu nome e a hora. Depois de pago, não muda."
        />
        <Button
          disabled={amount === null || amount <= 0}
          loading={inform.isPending}
          onClick={() => inform.mutate()}
        >
          Gravar valor
        </Button>
      </div>
    </div>
  );
}

function CashCard({
  rows,
  loading,
  canPay,
}: {
  rows: DispatchRow[];
  loading: boolean;
  canPay: boolean;
}) {
  const [paying, setPaying] = useState<number | null>(null);
  return (
    <Card
      title="À vista a pagar"
      descricao={
        canPay
          ? 'Um por vez, com o comprovante. Vai ao Financeiro já pago, sem revisão.'
          : 'Informar o à vista é dos líderes da Assistência 24h.'
      }
      bleed
    >
      <Table
        paginate={false}
        label="À vista a pagar"
        items={rows}
        loading={loading}
        rowKey={(d) => String(d.id)}
        empty="Nada à vista a pagar."
        expanded={canPay && paying !== null ? String(paying) : null}
        onExpandedChange={(key) => setPaying(key === null ? null : Number(key))}
        expand={(d) => (canPay ? <PayForm d={d} onDone={() => setPaying(null)} /> : null)}
        columns={[
          {
            title: 'Acionamento',
            alignment: 'inicio' as const,
            cell: (d: DispatchRow) => <span className="font-mono text-xs">{d.code}</span>,
          },
          { title: 'Prestador', cell: (d: DispatchRow) => d.providerName },
          {
            title: 'Associado',
            cell: (d: DispatchRow) => `${d.memberName ?? ''} · ${d.plate ?? ''}`,
          },
          { title: 'Quando', cell: (d: DispatchRow) => when(d.executedAt) },
          {
            title: 'Valor',
            alignment: 'fim' as const,
            cell: (d: DispatchRow) => (
              <span className="tabular-nums">{reais(d.amount)}</span>
            ),
          },
          {
            title: '',
            cell: (d: DispatchRow) =>
              canPay && (
                <Button
                  size="sm"
                  onClick={() => setPaying(paying === d.id ? null : d.id)}
                >
                  {paying === d.id ? 'Fechar' : 'Pagar'}
                </Button>
              ),
          },
        ]}
      />
    </Card>
  );
}

function PayForm({ d, onDone }: { d: DispatchRow; onDone: () => void }) {
  const client = useQueryClient();
  const today = businessDate(new Date());
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState<SettlementMethod | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const pay = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.payCash({
          body: {
            dispatchId: d.id,
            paidOn,
            method: method as SettlementMethod,
            receipt: await fileBody(file!),
          },
        }),
        201,
        'Não foi possível registrar o pagamento.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
      onDone();
    },
  });
  const dateOk = paidOn !== '' && paidOn <= today;
  return (
    <div className="grid gap-3 p-3">
      {pay.error && <Alert tone="erro">{pay.error.message}</Alert>}
      <div className="flex flex-wrap items-end gap-3">
        <DateField
          label="Pago em"
          required
          value={paidOn}
          onChange={setPaidOn}
          error={paidOn > today ? 'O pagamento é de hoje ou de antes.' : null}
        />
        <SearchSelect
          label="Como foi pago"
          required
          options={(Object.keys(METHOD_LABEL) as SettlementMethod[]).map((m) => ({
            value: m,
            label: METHOD_LABEL[m],
          }))}
          value={method}
          onChange={(v) => setMethod(v as SettlementMethod | '')}
        />
        <Field
          label="Comprovante"
          required
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-texto-suave">
          Vai pela Stone, em PROTEÇÃO AUTO, na categoria do serviço. A despesa é lançada
          no Conta Azul em aberto, e a baixa vem quando o Financeiro conciliar a Stone.
        </p>
        <Button
          disabled={!dateOk || !method || !file || pay.isPending}
          loading={pay.isPending}
          onClick={() => pay.mutate()}
        >
          Registrar pagamento de {reais(d.amount)}
        </Button>
      </div>
    </div>
  );
}

function MonthlyCard({
  rows,
  loading,
  canClose,
}: {
  rows: MonthlyRow[];
  loading: boolean;
  canClose: boolean;
}) {
  const [opened, setOpened] = useState<string | null>(null);
  const [filter, setFilter] = useState<{ provider: string; month: string }>({
    provider: '',
    month: '',
  });
  const keyOf = (m: MonthlyRow) => String(m.providerId);
  const months = [
    ...new Set(rows.flatMap((m) => m.dispatches.map((d) => monthOf(d.executedAt)))),
  ].sort();
  /* O mês peneira os acionamentos de cada prestador; quem fica sem nenhum sai. */
  const shown = rows
    .filter((m) => filter.provider === '' || String(m.providerId) === filter.provider)
    .map((m) => {
      if (filter.month === '') return m;
      const dispatches = m.dispatches.filter(
        (d) => monthOf(d.executedAt) === filter.month,
      );
      return {
        ...m,
        dispatches,
        dispatchCount: dispatches.length,
        total: toColumn(sum(...dispatches.map((d) => moneyOf(d.amount)))),
        withoutAmount: dispatches.filter((d) => d.amount === null).length,
      };
    })
    .filter((m) => m.dispatches.length > 0);
  return (
    <Card
      title="Mensal"
      descricao="Os acionamentos ainda sem acerto, por prestador e de qualquer mês. Marque os que entram, digite o valor dos que saíram sem valor, como o prestador repassou, e gere o lote: a despesa vai ao Financeiro, que revisa e paga."
      bleed
    >
      <Table
        paginate={false}
        label="Mensal"
        toolbar={
          <Filters
            values={filter}
            fields={[
              {
                type: 'selecao',
                name: 'provider',
                label: 'Prestador',
                placeholder: 'Todos',
                options: rows.map((m) => ({
                  value: String(m.providerId),
                  label: m.providerName,
                })),
              },
              {
                type: 'selecao',
                name: 'month',
                label: 'Mês do acionamento',
                placeholder: 'Todos',
                options: months.map((ym) => ({ value: ym, label: monthLabel(ym) })),
              },
            ]}
            onApply={(values) =>
              setFilter({
                provider: String(values.provider ?? ''),
                month: String(values.month ?? ''),
              })
            }
          />
        }
        items={shown}
        loading={loading}
        rowKey={keyOf}
        empty={
          rows.length === 0 ? 'Nenhum acionamento mensal a pagar.' : 'Nada neste recorte.'
        }
        expanded={opened}
        onExpandedChange={setOpened}
        expand={(m) => (
          <BatchForm
            key={`${m.providerId}-${filter.month}`}
            m={m}
            canClose={canClose}
            onDone={() => setOpened(null)}
          />
        )}
        columns={[
          {
            title: 'Prestador',
            alignment: 'inicio' as const,
            cell: (m) => <b>{m.providerName}</b>,
          },
          {
            title: 'Meses',
            cell: (m) =>
              [...new Set(m.dispatches.map((d) => monthOf(d.executedAt)))]
                .sort()
                .map(monthLabel)
                .join(', '),
          },
          { title: 'Acionamentos', cell: (m) => m.dispatchCount },
          {
            title: 'Total',
            alignment: 'fim' as const,
            cell: (m) => (
              <span className="flex flex-col items-end gap-0.5">
                <span className="tabular-nums">{reais(m.total)}</span>
                {m.withoutAmount > 0 && (
                  <Badge tone="alerta">{m.withoutAmount} sem valor</Badge>
                )}
              </span>
            ),
          },
          {
            title: '',
            cell: (m) => (
              <div className="flex items-center justify-end gap-2">
                {!m.hasBeneficiary && <Badge tone="alerta">Sem dados bancários</Badge>}
                <Button
                  size="sm"
                  variant={canClose && m.hasBeneficiary ? undefined : 'secundario'}
                  onClick={() => setOpened(opened === keyOf(m) ? null : keyOf(m))}
                >
                  {opened === keyOf(m)
                    ? 'Fechar'
                    : canClose && m.hasBeneficiary
                      ? 'Montar lote'
                      : 'Ver acionamentos'}
                </Button>
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
}

/**
 * Os acionamentos do prestador com a caixa de marcar, o total do que está
 * marcado por categoria do Conta Azul, e o botão do lote. Nasce com tudo
 * marcado; quem não pode gerar o lote só vê a lista.
 *
 * O que saiu sem valor (02/11/2026) ganha o campo do valor na própria linha:
 * o operador não sabe o valor do mensal, o prestador repassa, e a líder o
 * digita aqui. Marcado sem valor, o lote não sai.
 */
function BatchForm({
  m,
  canClose,
  onDone,
}: {
  m: MonthlyRow;
  canClose: boolean;
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [marked, setMarked] = useState<Set<number>>(
    () => new Set(m.dispatches.map((d) => d.id)),
  );
  const [file, setFile] = useState<File | null>(null);
  const [typed, setTyped] = useState<Record<number, string>>({});
  const chosen = m.dispatches.filter((d) => marked.has(d.id));
  /* O valor de cada linha: o que ela tem, ou o que a líder digitou. */
  const valueOf = (d: MonthlyDispatch): Money | null => {
    if (d.amount !== null) return moneyOf(d.amount);
    const v = parseMoneyInput(typed[d.id] ?? '');
    return v !== null && v > 0 ? v : null;
  };
  const blankChosen = chosen.filter((d) => valueOf(d) === null);
  const total = sum(...chosen.map((d) => valueOf(d) ?? ZERO));
  const byCategory = new Map<string, Money>();
  for (const d of chosen) {
    const key = d.category ?? 'Sem categoria';
    byCategory.set(key, sum(byCategory.get(key) ?? ZERO, valueOf(d) ?? ZERO));
  }
  const withoutCategory = chosen.filter((d) => d.category === null);
  const toggle = (id: number) =>
    setMarked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOn = marked.size === m.dispatches.length;

  const create = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.createBatch({
          body: {
            providerId: m.providerId,
            dispatchIds: chosen.map((d) => d.id),
            amounts: chosen
              .filter((d) => d.amount === null)
              .map((d) => ({ dispatchId: d.id, amount: toColumn(valueOf(d)!) })),
            invoice: file ? await fileBody(file) : null,
          },
        }),
        201,
        'Não foi possível gerar o lote.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
      onDone();
    },
  });
  const selectable = canClose && m.hasBeneficiary;

  return (
    <div className="grid gap-3 p-3">
      {create.error && <Alert tone="erro">{create.error.message}</Alert>}
      <table className="w-full text-sm">
        <caption className="sr-only">Acionamentos de {m.providerName}</caption>
        <thead>
          <tr className="text-left text-xs text-texto-suave">
            {selectable && (
              <th className="w-8 py-1 font-semibold">
                <input
                  type="checkbox"
                  aria-label="Marcar todos"
                  checked={allOn}
                  onChange={() =>
                    setMarked(allOn ? new Set() : new Set(m.dispatches.map((d) => d.id)))
                  }
                  className="size-4 rounded border-borda-forte accent-primaria-texto"
                />
              </th>
            )}
            <th className="py-1 font-semibold">Acionamento</th>
            <th className="py-1 font-semibold">Quando</th>
            <th className="py-1 font-semibold">Categoria</th>
            <th className="py-1 font-semibold">Serviço</th>
            <th className="py-1 font-semibold">Associado</th>
            <th className="py-1 text-right font-semibold">Valor</th>
          </tr>
        </thead>
        <tbody>
          {m.dispatches.map((d: MonthlyDispatch) => (
            <tr key={d.id} className="border-t border-borda">
              {selectable && (
                <td className="py-1.5">
                  <input
                    type="checkbox"
                    aria-label={`Marcar ${d.code}`}
                    checked={marked.has(d.id)}
                    onChange={() => toggle(d.id)}
                    className="size-4 rounded border-borda-forte accent-primaria-texto"
                  />
                </td>
              )}
              <td className="py-1.5 font-mono text-xs">{d.code}</td>
              <td className="py-1.5 tabular-nums text-texto-suave">
                {when(d.executedAt)}
              </td>
              <td className="py-1.5">
                {d.category ?? <Badge tone="alerta">Falta o motivo</Badge>}
              </td>
              <td className="py-1.5">{d.serviceName}</td>
              <td className="py-1.5">
                {d.memberName ?? ''} · {d.plate ?? ''}
              </td>
              <td className="py-1.5 text-right tabular-nums">
                {d.amount !== null ? (
                  reais(d.amount)
                ) : selectable ? (
                  <div className="ml-auto w-36">
                    <Field
                      label={`Valor de ${d.code}`}
                      labelHidden
                      mask="money"
                      prefix="R$"
                      placeholder="Valor repassado"
                      value={typed[d.id] ?? ''}
                      onChange={(e) =>
                        setTyped((prev) => ({ ...prev, [d.id]: e.target.value }))
                      }
                    />
                  </div>
                ) : (
                  <Badge tone="alerta">Sem valor</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {selectable && (
        <>
          {chosen.length > 0 && (
            <dl className="grid gap-1 text-sm">
              <dt className="text-xs font-semibold text-texto-suave">
                Por categoria no Conta Azul
              </dt>
              {[...byCategory].map(([name, value]) => (
                <dd key={name} className="flex justify-between gap-3">
                  <span>{name}</span>
                  <span className="tabular-nums">{reais(toColumn(value))}</span>
                </dd>
              ))}
            </dl>
          )}
          {blankChosen.length > 0 && (
            <Alert tone="alerta">
              Falta o valor de {blankChosen.map((d) => d.code).join(', ')}: digite o que o
              prestador repassou, ou desmarque.
            </Alert>
          )}
          {withoutCategory.length > 0 && (
            <Alert tone="alerta">
              {withoutCategory.map((d) => d.code).join(', ')} sem o motivo do reboque ou o
              serviço residencial. Abra o acionamento e escolha antes de gerar o lote.
            </Alert>
          )}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Field
              label="Nota do prestador (opcional)"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-texto-suave">
              A competência é hoje; o vencimento o Financeiro preenche ao autorizar.
            </p>
            <Button
              disabled={
                chosen.length === 0 ||
                withoutCategory.length > 0 ||
                blankChosen.length > 0 ||
                create.isPending
              }
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Gerar lote de {chosen.length} acionamento(s) · {reais(toColumn(total))}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
